import React from "react";
import { Link } from "react-router-dom";
import Card from "../../../components/ui-components/Card";
import { scopeToSearchParams } from "../../cost-analyzer/useAnalyzerScope";
import { formatVariance } from "../status";

const money = (value) => {
  if (value == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
};

// This tab deliberately does NOT embed AnalyzerView. The analyzer owns the
// global filter store, and its filter bar would let a user silently escape the
// use-case scope while the page still claimed to be about one use case. Full
// exploration is one click away instead.
//
// data-testid="financials-tab" on the root is the stable marker
// UseCaseDetailPage's tab-switching tests assert on to prove this tab body
// rendered; kept from the Task 10 placeholder so those tests need no change.
export default function FinancialsTab({ useCase }) {
  const row = useCase?.row ?? {};

  const params = scopeToSearchParams({
    dim: "use_case",
    ownerFilters: [
      { dimensionCode: "use_case", nodes: [{ id: row.nodeId, label: row.name }] },
    ],
  });
  // scopeToSearchParams already percent-encodes each `owner`/`filters` value
  // internally, and `new URLSearchParams(params).toString()` encodes it again
  // for transport — matching InvestigationDetailPage.jsx's "Open analysis"
  // link. That second encoding is deliberate, not redundant: on read, the
  // analyzer's `searchParams.get("owner")` undoes exactly one layer, then
  // searchParamsToScope's own decodeURIComponent undoes the other. Skipping
  // this second encode would leave a raw, unescaped label (e.g. one
  // containing a literal `%`) sitting in the URL, which decodeURIComponent
  // would then fail to parse on the other end.
  const analyzerHref = `/cost-analyzer?${new URLSearchParams(params).toString()}`;

  return (
    <div data-testid="financials-tab" className="flex flex-col gap-4">
      <Card>
        <div className="p-4 flex flex-wrap gap-6">
          <div>
            <div className="text-text-2 text-xs uppercase">Actual</div>
            <div className="text-2xl">{money(row.rolledCost)}</div>
          </div>
          <div>
            <div className="text-text-2 text-xs uppercase">Budget</div>
            <div className="text-2xl">{row.budget ? money(row.budget.capEur) : "—"}</div>
          </div>
          <div>
            <div className="text-text-2 text-xs uppercase">Forecast</div>
            <div className="text-2xl">{money(row.forecast)}</div>
          </div>
          <div>
            <div className="text-text-2 text-xs uppercase">Variance</div>
            <div className="text-2xl">{formatVariance(row.variance)}</div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-4">
          <Link to={analyzerHref} className="underline">
            Open in Cloud &amp; AI Spend
          </Link>
          <div className="text-text-2 text-sm">
            Explore this use case across providers, services and models, with the
            scope already applied.
          </div>
        </div>
      </Card>
    </div>
  );
}
